'use client';

import * as React from 'react';

import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';
import type { DayStripChip, DayStripScope } from '@/lib/discover/dayStrip';

/** sr-only notes, verbatim from the v3.1 mock and the DQ-3 default. */
export const DAY_STRIP_FAR_NOTE =
  'Not loaded yet. Jumps to the end of the list where more days load.';
export const DAY_STRIP_EMPTY_NOTE = 'No events on this day';
/**
 * DQ-4: under the Price sort the server orders by that key, so a page
 * can add rows to any day — a far day is not necessarily at the tail.
 */
export const DAY_STRIP_FAR_NOTE_UNORDERED = 'Not loaded yet. Load more at the end of the list';

export interface DiscoverDayStripProps {
  /** From `buildDayStripChips` — one per city-local day, plus an optional leading on-now. */
  readonly chips: readonly DayStripChip[];
  readonly scope: DayStripScope;
  /** Render the trailing `+ 30 days →` link (hidden on tonight and on next30, DQ-6). */
  readonly showMoreLink: boolean;
  /** Destination of the tail link — the current filters with `date=next30`. */
  readonly moreHref: string;
  /**
   * The chip whose day section sits under the bar (`aria-current="date"`).
   * Owned by the scroll-spy; `undefined` marks no chip current.
   */
  readonly activeDate?: string;
  /**
   * Fired when a chip that can jump is pressed (never for `empty`, DQ-3). The
   * jump itself — loaded day vs list tail — belongs to the caller.
   */
  readonly onChipSelect?: (chip: DayStripChip) => void;
  /** sr-only note on far chips; {@link DAY_STRIP_FAR_NOTE} by default. */
  readonly farNote?: string;
  readonly className?: string;
}

/**
 * Discover day strip (WEB-1079 — v3.1 `.dv-days`): the date anchor that rides
 * the context bar. A `<nav>` of real `<button>` chips, mono weekday + display
 * day number, 34px paint on the `.a-hit-floor` coarse 44px guard. Paint lives
 * in `styles/components/discover-day-strip.css`.
 *
 * Chip states come from `buildDayStripChips`:
 * - `loaded` — the day's section is in the list.
 * - `far` — in range, not loaded yet; described by the "Not loaded yet" note.
 * - `empty` — no events that day: `aria-disabled`, described by
 *   "No events on this day", and never fires `onChipSelect` (DQ-3).
 * - `on-now` — the single leading chip for in-progress events whose section
 *   is dated before the range (DQ-5).
 *
 * Every chip carries `data-local-date` (the `#discover-day-YYYY-MM-DD` key) and
 * `data-state`, which the scroll-spy and jump wiring read.
 */
export const DiscoverDayStrip = React.forwardRef<HTMLElement, DiscoverDayStripProps>(
  function DiscoverDayStrip(
    {
      chips,
      scope,
      showMoreLink,
      moreHref,
      activeDate,
      onChipSelect,
      farNote = DAY_STRIP_FAR_NOTE,
      className,
    },
    ref,
  ) {
    const noteId = React.useId();
    const farNoteId = `${noteId}-far`;
    const emptyNoteId = `${noteId}-empty`;
    const hasFar = chips.some((chip) => chip.state === 'far');
    const hasEmpty = chips.some((chip) => chip.state === 'empty');

    return (
      <nav
        ref={ref}
        aria-label="Days in range"
        data-testid="discover-day-strip"
        data-scope={scope}
        className={cn('dv-days', className)}
      >
        {chips.map((chip) => {
          const isEmpty = chip.state === 'empty';
          const describedBy = chip.state === 'far' ? farNoteId : isEmpty ? emptyNoteId : undefined;
          return (
            <button
              key={`${chip.state === 'on-now' ? 'on-now-' : ''}${chip.localDate}`}
              type="button"
              className="dv-dayc a-hit-floor"
              data-testid="discover-day-chip"
              data-local-date={chip.localDate}
              data-state={chip.state}
              aria-current={chip.localDate === activeDate ? 'date' : 'false'}
              aria-describedby={describedBy}
              aria-disabled={isEmpty || undefined}
              onClick={() => {
                if (!isEmpty) onChipSelect?.(chip);
              }}
            >
              {chip.state === 'on-now' ? (
                <span>On now</span>
              ) : (
                <>
                  <span>
                    {chip.weekday}
                    {scope === 'tonight' ? ' · tonight' : null}
                  </span>{' '}
                  <b>{chip.dayNumber}</b>
                </>
              )}
            </button>
          );
        })}
        {showMoreLink ? (
          <a
            href={moreHref}
            className="dv-days__more a-hit-floor"
            data-testid="discover-day-strip-more"
          >
            <span className="inline-flex items-center gap-1">
              + 30 days
              <TextIcon />
            </span>
          </a>
        ) : null}
        {hasFar ? (
          <span id={farNoteId} className="sr-only">
            {farNote}
          </span>
        ) : null}
        {hasEmpty ? (
          <span id={emptyNoteId} className="sr-only">
            {DAY_STRIP_EMPTY_NOTE}
          </span>
        ) : null}
      </nav>
    );
  },
);
